import { AttackPattern, PatternState } from '../AttackPattern.js';

/**
 * Ember Wake (Ashen Throne's signature) — the floor remembers where you
 * walked. While the pattern runs, the ground under the player smolders on
 * a short delay and then IGNITES into a persistent burning patch — so your
 * own path becomes area denial you authored. The Unblinking punishes
 * moving and Déjà Vu chases where you were; this instead forbids
 * RETURNING: circle lazily and you wall yourself in with your own fire,
 * move with intent and you leave a clean compact trail. Sampling stops
 * early so every patch burns out inside the pattern's own window (no
 * invisible leftover hazards), and dash i-frames let you cross a burning
 * patch in an emergency.
 */
export class EmberWake extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Ember Wake', telegraphDuration: 0.8, activeDuration: 5.6, minPhase });
    this.config = config;
  }

  onBegin() {
    this._elapsed = 0;
    this._sampleTimer = 0;
    this._smolders = []; // { x, y, fuse } — warning stage, not yet harmful
    this._patches = []; // { x, y, life } — burning ground
    this._burnTimer = 0;
  }

  onActiveTick(dt, boss, player, _pool) {
    this._elapsed += dt;
    this._burnTimer -= dt;

    // Footstep sampling — only during the first stretch, so the last
    // patch (sample + fuse + burn) expires before the pattern ends.
    if (this._elapsed < 2.3) {
      this._sampleTimer -= dt;
      if (this._sampleTimer <= 0) {
        this._sampleTimer = this.config.sampleInterval - boss.phase * 0.05;
        this._smolders.push({ x: player.x, y: player.y, fuse: this.config.igniteDelay });
      }
    }

    for (let i = this._smolders.length - 1; i >= 0; i--) {
      this._smolders[i].fuse -= dt;
      if (this._smolders[i].fuse <= 0) {
        const { x, y } = this._smolders[i];
        this._smolders.splice(i, 1);
        this._patches.push({ x, y, life: this.config.patchDuration });
      }
    }

    for (let i = this._patches.length - 1; i >= 0; i--) {
      const patch = this._patches[i];
      patch.life -= dt;
      if (patch.life <= 0) {
        this._patches.splice(i, 1);
        continue;
      }
      if (this._burnTimer <= 0 && !player.isInvincible
        && Math.hypot(player.x - patch.x, player.y - patch.y) < this.config.patchRadius + player.radius) {
        player.takeDamage(this.config.tickDamage);
        this._burnTimer = 0.45;
      }
    }
  }

  getVisual() {
    if (this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'emberWake',
      radius: this.config.patchRadius,
      smolders: this._smolders.map((s) => ({ x: s.x, y: s.y, t: 1 - s.fuse / this.config.igniteDelay })),
      patches: this._patches.map((p) => ({ x: p.x, y: p.y, life: p.life / this.config.patchDuration })),
    };
  }
}
